import { MasterDataStatusKeyInfo } from '@/items/MasterDataStatus';
import { OrderStatusKeyInfo } from '@/items/OrderStatus';
import { PurchaseOrder, PurchaseOrderKeyInfo } from '@/models/PurchaseOrder';
import ErrorHandler from '@/support/ErrorHandler';
import { cloneDeep } from 'lodash';
import { Component, Prop, PropSync, Vue } from 'vue-property-decorator';

@Component<PurchaseOrderEditableCard>({})
export default class PurchaseOrderEditableCard extends Vue {

  // #region @PropSyncs

  @PropSync('purchaseOrderSync')
  protected purchaseOrder!: null | PurchaseOrder;

  @PropSync('isEditingSync')
  protected isEditing!: boolean;

  // #endregion

  // #region @Props

  @Prop({ default: true })
  protected isLoading!: boolean;

  // #endregion

  // #region @Refs
  // #endregion

  // #region Props

  protected isFormValid = true;

  protected isUpdating = false;

  protected purchaseOrderClone: null | PurchaseOrder = null;

  protected PurchaseOrderKeyInfo = PurchaseOrderKeyInfo;

  protected OrderStatusKeyInfo = OrderStatusKeyInfo;

  protected MasterDataStatusKeyInfo = MasterDataStatusKeyInfo;

  // #endregion

  // #region Plugin props
  // #endregion

  // #region Lifecycle hooks / Init
  // #endregion

  // #region Handlers

  protected onEdit(): void {
    this.purchaseOrderClone = cloneDeep(this.purchaseOrder);
    this.isEditing = true;
  }

  protected onReset(): void {
    this.purchaseOrder = cloneDeep(this.purchaseOrder);
    this.purchaseOrderClone = null;
    this.isEditing = false;
  }

  protected async onUpdate(): Promise<void> {
    if (!this.purchaseOrder) { return; }

    this.isUpdating = true;
    await this.update();
    this.isUpdating = false;
    this.isEditing = false;
  }

  // #endregion

  // #region Helpers
  // #endregion

  // #region API Requests

  protected async update(): Promise<void> {
    if (!this.purchaseOrderClone) { return; }

    const updatePayload = this.updatePayload;

    if (! this.updatePayload) { return; }

    try {
      this.purchaseOrder = await this.purchaseOrderClone
        .include(['brand', 'note', 'supplier'])
        .update(this.updatePayload);
    } catch (error) {
      ErrorHandler.network(error);
    }
  }

  // #endregion

  // #region Getters & Setters

  protected get canSave(): boolean {
    if (!this.purchaseOrderClone) { return false; }

    if (
      !this.purchaseOrderClone.brand
      || (
        typeof this.purchaseOrderClone.brand !== 'string'
        && !this.purchaseOrderClone.brand.id
      )
    ) { return false; }

    if (
      !this.purchaseOrderClone.supplier
      || (
        typeof this.purchaseOrderClone.supplier !== 'string'
        && !this.purchaseOrderClone.supplier.id
      )
    ) { return false; }

    return this.isFormValid;
  }

  protected get gap(): string {
    return ['sm', 'lg', 'xl'].includes(this.$vuetify.breakpoint.name)
      ? '16'
      : '0';
  }

  protected get updatePayload(): null | PurchaseOrder {
    if (!this.purchaseOrderClone) { return null; }

    const payload: PurchaseOrder = cloneDeep(this.purchaseOrderClone);

    if (
      ! (
        payload.brand
        && payload.supplier
      )
    ) { return null; }

    if (payload.brand && typeof payload.brand === 'object') {
      payload.brand = payload.brand.id;
    }

    if (payload.supplier && typeof payload.supplier === 'object') {
      payload.supplier = payload.supplier.id;
    }

    if (payload.master_data_status && typeof payload.master_data_status === 'object') {
      payload.master_data_status = (payload.master_data_status as any).id;
    }

    if (payload.status && typeof payload.status === 'object') {
      payload.status = (payload.status as any).id;
    }

    return payload;
  }

  protected get purchaseOrderOrderStatusText(): string {
    if (!this.purchaseOrder || !this.purchaseOrder.status) { return ''; }

    return OrderStatusKeyInfo[this.purchaseOrder.status]
      && OrderStatusKeyInfo[this.purchaseOrder.status].labelShort
      ? OrderStatusKeyInfo[this.purchaseOrder.status].labelShort
      : this.purchaseOrder.status;
  }

  protected get purchaseOrderMasterDataStatusText(): string {
    if (!this.purchaseOrder || !this.purchaseOrder.master_data_status) { return ''; }

    return MasterDataStatusKeyInfo[this.purchaseOrder.master_data_status]
      && MasterDataStatusKeyInfo[this.purchaseOrder.master_data_status].labelShort
      ? MasterDataStatusKeyInfo[this.purchaseOrder.master_data_status].labelShort
      : this.purchaseOrder.master_data_status;
  }

  // #endregion

  // #region @Watchers
  // #endregion
}
